import { UserRole } from 'business/definitions';

import { modulesWithSubRoles } from '../components/UserFormGeneralSection/components/helpers/constants';
import { ModuleTree } from 'routes/Admin/UserManagement/Users/CreateOrEditUser/components/UserFormGeneralSection/components/types';
import { isRoleTreeWithPermissions } from 'routes/Admin/UserManagement/Users/CreateOrEditUser/components/UserFormGeneralSection/components/helpers/typeHelpers';

/**
 * Given a moduleTree, (see modulesWithSubRoles constant), returns all subroles and permissions.
 * @param  {ModuleTree} moduleWithSubRoles - the module from which we want to extract all subroles and permissions.
 */
const getAllSubRoles = (moduleWithSubRoles: ModuleTree) =>
    moduleWithSubRoles.roles.flatMap((role) =>
        isRoleTreeWithPermissions(role)
            ? role.permissions.map((permission) => permission.permissionName)
            : role.roleName,
    );

/**
 * Helper to remove all the roles that do not make sense if their parent role is removed.
 * For instance, if we want to remove the budget user role from a user, we should remove as well his budget admin role if exists.
 * These cases are listed in the modulesWithSubRoles constant.
 * @param  {UserRole[]} roles - Current user roles
 * @param  {UserRole} roleToRemove - The role to remove from the current user roles
 */
export const removeRoleFromRoles = (
    roles: UserRole[],
    roleToRemove: UserRole,
): UserRole[] => {
    const filteredRoles = roles.filter((role) => role !== roleToRemove);
    const moduleWithSubRoles = modulesWithSubRoles.find(
        (module) => module.moduleRole === roleToRemove,
    );

    return !moduleWithSubRoles
        ? filteredRoles
        : filteredRoles.filter(
              (role) => !getAllSubRoles(moduleWithSubRoles).includes(role),
          );
};

/**
 * Helper to make sure the role to add to the current user roles is allowed.
 * It may not be the case if:
 * - the role already exists in the current user roles.
 * - the parent role does not exist in the current user roles list.
 * For instance, if we want to provide a user with a budget admin role, this user should already be a budget user.
 * These cases are listed in the modulesWithSubRoles constant.
 * @param  {UserRole[]} roles - Current user roles
 * @param  {UserRole} roleToAdd - The role to add to the current user roles
 */
export const addRoleToRoles = (
    roles: UserRole[],
    roleToAdd: UserRole,
): UserRole[] => {
    if (roles.includes(roleToAdd)) return roles;

    const moduleWhereRoleToAddIsChild = modulesWithSubRoles.find((module) => {
        const rolesNames = module.roles.map((roleTree) => roleTree.roleName);
        return rolesNames.includes(roleToAdd);
    });

    if (
        moduleWhereRoleToAddIsChild &&
        !roles.includes(moduleWhereRoleToAddIsChild.moduleRole)
    ) {
        return roles;
    }
    return [...roles, roleToAdd];
};
